import { defineAsyncComponent } from 'vue'
import { ApolloCache } from '@apollo/client/cache'
import { getViziDataGqlQuery } from './api'
import { type MockupType, type Vizi } from './types'
import { type DesignChallengeSubmission } from '/@/common/designChallenges/types'
import type { Project } from '../projects/types'
import { useTracker } from '/@/core/tracking/api'
import { useNewViziModal } from './modals/NewViziModal'
import { stellaModal } from '/@/stella/StellaModal'
import { useMobileGuard } from './mobileGuard'
import { usePermission } from '../tiers'
import { useProjectGuard } from '../projects/utils'

export const BLANK_MOCKUP_ID = '216356'
export const BLANK_EDITORIAL_ID = '593047'
export const BLANK_FLOOR_PLAN_ID = '228707'
export const BLANK_ELEVATION_ID = '404387'

export const CUSTOM_BACKGROUND_BLANK_FLOOR_PLAN_ID = '644432'
export const CUSTOM_BACKGROUND_BLANK_ELEVATION_ID = '644434'

export const BLANK_IDS = [
  BLANK_MOCKUP_ID,
  BLANK_EDITORIAL_ID,
  BLANK_FLOOR_PLAN_ID,
  BLANK_ELEVATION_ID,
  CUSTOM_BACKGROUND_BLANK_FLOOR_PLAN_ID,
  CUSTOM_BACKGROUND_BLANK_ELEVATION_ID,
]

export function isBlankTemplate(id?: string) {
  return id?.length && BLANK_IDS.includes(id)
}

export function getBlankTemplateId(
  mockupType: MockupType,
  customBackground?: boolean,
) {
  switch (mockupType) {
    case 'MOCKUP':
      return BLANK_MOCKUP_ID
    case 'EDITORIAL':
      return BLANK_EDITORIAL_ID
    case 'FLOOR_PLAN':
      return customBackground
        ? CUSTOM_BACKGROUND_BLANK_FLOOR_PLAN_ID
        : BLANK_FLOOR_PLAN_ID
    case 'ELEVATION':
      return customBackground
        ? CUSTOM_BACKGROUND_BLANK_ELEVATION_ID
        : BLANK_ELEVATION_ID
    default:
      return BLANK_MOCKUP_ID
  }
}

export function useViziCache() {
  function getCachedVizi(cache: ApolloCache<unknown>, viziId: string) {
    return cache.readQuery<{ mockup: Vizi }>({
      query: getViziDataGqlQuery,
      variables: {
        id: viziId,
      },
    })?.mockup
  }

  function addDesignChallengeSubmission(
    cache: ApolloCache<unknown>,
    designChallengeSubmission: DesignChallengeSubmission,
    viziId: string,
  ) {
    const cachedVizi = getCachedVizi(cache, viziId)
    if (!cachedVizi) return
    const id = cache.identify({ ...cachedVizi })
    if (!id) return
    cache.modify({
      id,
      fields: {
        ['designChallengeSubmission']() {
          return designChallengeSubmission
        },
      },
    })
  }

  return { addDesignChallengeSubmission }
}

export function useNewViziApi() {
  const tracker = useTracker()
  const newViziModal = useNewViziModal()
  const mobileGuard = useMobileGuard()
  const projectGuard = useProjectGuard()
  const permission = usePermission('CREATE_VIZIS')

  function openModal({
    type,
    project,
    trackingData,
    withPermission,
    offscreen,
  }: {
    type?: MockupType
    project?: Project
    withPermission?: boolean
    offscreen?: boolean
    trackingData?: { name: string; [key: string]: unknown }
  }) {
    if (trackingData) {
      const { name, ...props } = trackingData
      tracker.send(name, props)
    }

    const viziTypes: MockupType[] =
      type === 'FLOOR_PLAN' || type === 'ELEVATION'
        ? ['FLOOR_PLAN', 'ELEVATION']
        : ['MOCKUP', 'EDITORIAL']

    if (!mobileGuard.featureAllowed.value) {
      stellaModal.open({
        content: defineAsyncComponent(
          () => import('/@/common/viz/modals/MobileGuardModalContent.vue'),
        ),
        data: {
          viziTypes,
        },
      })
      return
    }

    if (withPermission) {
      permission.tierGuard(
        async () => {
          try {
            const verifiedProject =
              project ?? (await projectGuard.verify('Vizi')).project

            newViziModal.show({
              offscreen,
              type,
              viziTypes,
              project: verifiedProject,
            })
          } catch {
            return
          }
        },
        {
          source: (trackingData?.component as string) ?? 'Not specified',
        },
      )
      return
    }

    newViziModal.show({
      offscreen,
      type,
      viziTypes,
      project: project ? { id: project.id, name: project.name } : undefined,
    })
  }

  return {
    openModal,
  }
}
